// Modules
import { Store, select } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { Component, Input, OnInit, EventEmitter, Output } from '@angular/core';

// Rxjs
import { Subscription } from 'rxjs';

//Constants
import {
  DATA_TABS,
  TAB_ACTION,
  ESTADO_RECLAMANTES_SAP,
  ESTADO_SINIESTRO,
  MOTIVO_FALLO_SINIESTRO,
  AUTO_ACCIDENT_ATTACHMENTS_STATUS,
} from '@accidents/shared/constants';

// Store - Reducer
import { ModuleState } from '@accidents/store/reducers';

// Store - Actions
import * as DetailAccidentActions from '@accidents/store/actions/detail-accident.actions';

// Store - Selectors
import * as DetailAccidentSelectors from '@accidents/store/selectors/detail-accident.selectors';

//Interfaces
import {
  IAccidentDetail,
  IAccidentEditableData,
} from '@accidents/shared/interfaces/accidents';
import { ICustomDropdown } from '@shared/interfaces';

@Component({
    selector: 'sbi-cot-back-accident-tabs-container',
    templateUrl: './accident-tabs-container.component.html',
    styleUrls: ['./accident-tabs-container.component.scss'],
    standalone: false
})
export class AccidentTabsContainerComponent implements OnInit {
  //pubic variables
  public tabs = DATA_TABS;
  public activeTab = DATA_TABS.DATA_ACCIDENT;
  public tabActionList = TAB_ACTION;
  public loading: boolean;
  public accidentEditableData: IAccidentEditableData = {};
  public errorEstadoFallido: string = null;
  public documentTypes: ICustomDropdown[] = [];
  public accidentDetail: IAccidentDetail = null;
  public hasClaimants: boolean = false;
  public estadoReclamantesSap = ESTADO_RECLAMANTES_SAP;
  public submitClaimantsSAPFailed: boolean = false;
  public submitClaimantsSAPWaiting: boolean = false;
  public accidentEstadoFallido: boolean = false;
  public accidentAttachmentsEstadoFallido: boolean = false;
  public errorEstadoAdjuntosFallido: string =
    'Hay adjuntos que no se pudieron enviar a SAP.';
  public puedeEditarMatriculaYChasis: boolean = false;
  public puedeEditarFechaHoraOcurrencia: boolean = false;
  @Input() tabAction: string;
  @Input() attemptedReprocess: boolean = false;

  // Outputs
  @Output() editAccidentData = new EventEmitter();
  @Output() puedeEditarMatriculaYChasisChange = new EventEmitter<boolean>();
  @Output()
  puedeEditarFechaHoraOcurrenciaChange = new EventEmitter<boolean>();
  @Output()
  accidentAttachmentsEstadoFallidoChange = new EventEmitter<boolean>();

  // Servicio detalle siniestros
  private _accidentDetailRequest$ = this._store$.pipe(
    select(DetailAccidentSelectors.selectDetailAccident)
  );

  private _subscriptions$: Subscription[] = [];

  // Servicio loading siniestros
  public loading$ = this._store$.pipe(
    select(DetailAccidentSelectors.selectDetailAccidentLoading)
  );

  constructor(
    private _route: ActivatedRoute,
    private _store$: Store<ModuleState>
  ) {}

  ngOnInit() {
    const accidentId = this._route.snapshot.paramMap.get('id');
    this.activeTab =
      this._route.snapshot.queryParamMap.get('activeTab') ||
      DATA_TABS.DATA_ACCIDENT;

    // Get request
    this._getAccidentDetail(accidentId);
    this._parseAccidentDetail();
  }

  //Envio los campos editables a la funcion de editar
  public editAccidentTabData(accidentEditableData: IAccidentEditableData) {
    this.editAccidentData.emit(accidentEditableData);
  }

  // Method to get a Request by ID
  private _getAccidentDetail(id: string): void {
    if (id) {
      this.loading = true;
      this._store$.dispatch(
        DetailAccidentActions.getAccidentDetailRequested({ payload: id })
      );
    }
  }

  // Method to get a request and fill de form
  private _parseAccidentDetail(): void {
    // Parseamos la respuesta de la solicitud
    this._subscriptions$.push(
      this._accidentDetailRequest$.subscribe(
        (accidentRequest: IAccidentDetail) => {
          if (accidentRequest) {
            this.accidentDetail = accidentRequest;
            this._setDataSiniestroFallido(accidentRequest);
            this._setDataAdjuntosFallidos(accidentRequest);
            this._setDataReclamantesFallidosOEnEspera(accidentRequest);
            this.hasClaimants =
              this.accidentDetail.reclamantes &&
              this.accidentDetail.reclamantes.length
                ? true
                : false;
          }
          this.loading = false;
        }
      )
    );
  }

  // Private method to set the accident status
  private _setDataSiniestroFallido(accidentRequest): void {
    this.errorEstadoFallido = accidentRequest.errorProcesamiento;
    this.accidentEstadoFallido =
      accidentRequest.estadoDeProceso == ESTADO_SINIESTRO.FALLIDO;
    this.puedeEditarMatriculaYChasis =
      this.accidentEstadoFallido &&
      accidentRequest.motivoFallo ===
        MOTIVO_FALLO_SINIESTRO.VEHICULO_NO_ENCONTRADO;
    this.puedeEditarFechaHoraOcurrencia =
      this.accidentEstadoFallido &&
      accidentRequest.motivoFallo ===
        MOTIVO_FALLO_SINIESTRO.INCONSISTENCIA_FECHAS;
    this.puedeEditarMatriculaYChasisChange.emit(
      this.puedeEditarMatriculaYChasis
    );
    this.puedeEditarFechaHoraOcurrenciaChange.emit(
      this.puedeEditarFechaHoraOcurrencia
    );
  }

  // Private method to set the attachments status
  private _setDataAdjuntosFallidos(accidentRequest): void {
    this.accidentAttachmentsEstadoFallido =
      accidentRequest.estadoDeAdjuntos ===
      AUTO_ACCIDENT_ATTACHMENTS_STATUS.FALLIDO;
    this.accidentAttachmentsEstadoFallidoChange.emit(
      this.accidentAttachmentsEstadoFallido
    );
  }

  // Private method to set the claimant status
  private _setDataReclamantesFallidosOEnEspera(accidentRequest): void {
    this.submitClaimantsSAPFailed = !!accidentRequest.reclamantes.find(
      reclamante =>
        reclamante.estadoDeProceso.toLowerCase() ==
        ESTADO_RECLAMANTES_SAP.FALLIDO.toLowerCase()
    );
    this.submitClaimantsSAPWaiting =
      !!accidentRequest.reclamantes.find(
        reclamante =>
          reclamante.estadoDeProceso.toLowerCase() ==
          ESTADO_RECLAMANTES_SAP.EN_ESPERA.toLowerCase()
      ) && !this.submitClaimantsSAPFailed;
  }
}
